import React, { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { STORAGE_KEYS } from 'utils/constants';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { DeleteIcon } from 'cokitchen-icons';
import CropImageModal from 'components/modals/crop-image-modal';
import { getFileSize } from 'utils/functions';

type Props = {
  setFileUrls: (images: string[]) => void;
  fileUrls: string[];
  aspectRatio?: number;
};

const FileUploaderWithCropTool = ({ fileUrls, setFileUrls, aspectRatio }: Props) => {
  const MAX_SIZE = 5;

  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const [file, setFile] = useState<File | null>(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [allowDrop, setAllowDrop] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [exceedSize, setExceedSize] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [percent, setPercent] = useState(0);
  const [imgSrc, setImgSrc] = useState('');
  const [croppedImage, setCroppedImage] = useState('');
  const [imgType, setImgType] = useState('');
  const [imgName, setImgName] = useState('');
  const [size, setSize] = useState(0);
  const [preview, setPreview] = useState('');

  const checkExceedSize = (size: number) => {
    if (size > MAX_SIZE * 1024 * 1024) {
      setExceedSize(true);
      return true;
    } else {
      setExceedSize(false);
      return false;
    }
  };

  const updateFiles = (files: FileList) => {
    if (files && files[0].size) {
      if (!checkExceedSize(files[0].size)) {
        setFile(files[0]);
        setImgType(files[0].type);
        setImgName(files[0].name);
      }
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasUploaded(false);
    const { files } = e.target;
    if (files) updateFiles(files);
  };

  const handleDragOver = (e: any) => {
    if (allowDrop) e.preventDefault();
  };

  const handleFileDrop = (e: any) => {
    if (allowDrop) {
      e.preventDefault();
      setHasUploaded(false);
      const files = e.dataTransfer.files;
      if (files) updateFiles(files);
    }
  };

  const uploadSingleFile = (image: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', image);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://file-api.getfoodcourt.com/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          setPercent(progress);
        }
      };
      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        if (data) {
          resolve(data.url);
        }
      };

      xhr.onerror = () => {
        reject(Error('Problem uploading image. Contact tech support'));
      };

      xhr.send(formData);
    });
  };

  const uploadImg = async (image: File) => {
    setUploading(true);
    setPercent(0);
    try {
      const response = await uploadSingleFile(image);
      setFileUrls([response]);
      setHasUploaded(true);
      setPercent(100);
    } catch {
      setFile(null);
      toast(<ToastMessage type='error' message='Problem encountered while uploading images' />);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (file) {
      setImgSrc(URL.createObjectURL(file));
      setShowCropModal(true);
    } else {
      if (fileUploadRef.current) fileUploadRef.current.value = '';
    }
  }, [file]);

  const removeImage = () => {
    setFile(null);
    setFileUrls([]);
    setHasUploaded(false);
    setUploading(false);
    setImgSrc('');
    setCroppedImage('');
    setImgType('');
    setImgName('');
    setPercent(0);
  };

  useEffect(() => {
    if (uploading) {
      setAllowDrop(false);
    } else {
      setPercent(0);
      setAllowDrop(true);
    }
  }, [uploading]);

  useEffect(() => {
    if (percent === 100) {
      setUploading(false);
    }
  }, [percent, uploading]);

  const closeCropModal = () => {
    if (!hasUploaded) {
      setFile(null);
      setImgSrc('');
      setCroppedImage('');
      setImgType('');
      setImgName('');
    }
    setShowCropModal(false);
  };

  const convertBlobUrlToBlob = async (blobUrl: string) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    setSize(blob.size);
    setPreview(blobUrl);
    return blob;
  };

  const blobToFile = (blobData: Blob, fileName: string, mimeType: string) => {
    const file = new File([blobData], fileName, { type: mimeType });
    return file;
  };

  const saveImage = async () => {
    setShowCropModal(false);
    const imageBlob = await convertBlobUrlToBlob(croppedImage);
    const imageFile = blobToFile(imageBlob, imgName, imgType);
    uploadImg(imageFile);
  };

  return (
    <div>
      {showCropModal && imgSrc && (
        <CropImageModal
          show={showCropModal}
          onClose={closeCropModal}
          src={imgSrc}
          setCroppedImage={setCroppedImage}
          imgType={imgType}
          saveImage={saveImage}
          circularCrop={false}
          aspectRatio={aspectRatio}
        />
      )}
      <div
        style={{
          backgroundImage: `url(${fileUrls[0]})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
        className='bg-[#FAFAFA] border-b-2 border-[#D5D6D7] py-[19px] cursor-pointer'
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        onClick={() => {
          if (!uploading) fileUploadRef?.current?.click();
        }}>
        <div className='flex justify-center'>
          <div className='text-center text-xs text-[#6F7376] h-[120px]'>
            {hasUploaded && fileUrls?.length ? (
              <div className='w-full h-full flex justify-center items-center'>
                <p className='font-medium text-[#fff] text-base'>Update image</p>
              </div>
            ) : (
              <Fragment>
                <div className='flex justify-center'>
                  <UploadIcon />
                </div>

                <p className='mt-1  font-medium'>
                  <span className='text-[#2E3031] underline cursor-pointer'>Click to upload</span>
                  <span className='ml-1'>or drag and drop</span>
                </p>
                <p className='mt-1'>SVG, PNG or JPG (Max. 500x500px)</p>
              </Fragment>
            )}
          </div>
        </div>
        <input ref={fileUploadRef} type='file' accept='image/*' hidden onChange={onFileChange} />
      </div>
      {exceedSize && <div className='mt-2 text-red-5 text-sm'>Image size exceeds 5mb</div>}
      {file ? (
        <div className='file-uploader_multiple'>
          <div className='!p-3 file-uploader_file'>
            <div
              className='file-uploader_file_main'
              style={{
                width: '100%'
              }}>
              <div className='file-uploader_file_main_inner'>
                <div
                  style={{
                    backgroundImage: `url(${preview})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}></div>
              </div>
              <div className='duration_box'>
                <div className='text'>
                  <span>{getFileSize(size)}</span>
                  <div />
                  {uploading ? <span>Uploading...</span> : <span>Upload Complete</span>}
                </div>
              </div>
              {hasUploaded && file && (
                <div role='button' onClick={removeImage} className='has_uploaded'>
                  <DeleteIcon />
                </div>
              )}
              {uploading && <span className='uploading_text'>{percent}%</span>}
            </div>

            {uploading && (
              <div className='is_uploading'>
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    transition: 'width .8s'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <span />
      )}
    </div>
  );
};
export default FileUploaderWithCropTool;
